import React, { useState, useEffect } from 'react'
import { Project, User, ProjectFilters as ProjectFiltersType, ProjectFormData } from '../../types'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import ProjectCard from '../../components/Projects/ProjectCard'
import ProjectForm from '../../components/Projects/ProjectForm'
import ProjectFilters from '../../components/Projects/ProjectFilters'
import toast from 'react-hot-toast'

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | undefined>()
  const [filters, setFilters] = useState<ProjectFiltersType>({
    isActive: true,
    search: ''
  })

  // 載入專案列表
  const loadProjects = async () => {
    try {
      setLoading(true)
      const projectsData = await projectService.getProjects(filters)
      setProjects(projectsData)
    } catch (error) {
      console.error('Failed to load projects:', error)
      toast.error('載入專案列表失敗')
    } finally {
      setLoading(false)
    }
  }

  // 載入用戶列表
  const loadUsers = async () => {
    try {
      const usersData = await userService.getUsers({ isActive: true })
      setUsers(usersData)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('載入用戶列表失敗')
    }
  }

  // 初始載入
  useEffect(() => {
    loadProjects()
    loadUsers()
  }, [])

  // 當篩選條件改變時重新載入
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadProjects()
    }, 300) // 防抖

    return () => clearTimeout(timeoutId)
  }, [filters])

  // 處理建立/編輯專案
  const handleSubmitProject = async (data: ProjectFormData) => {
    try {
      setFormLoading(true)
      
      if (editingProject) {
        // 更新專案
        await projectService.updateProject(editingProject.id, {
          name: data.name,
          description: data.description,
          color: data.color,
          isActive: data.isActive,
          memberIds: data.memberIds,
          budgetType: data.budgetType,
          budgetAmount: data.budgetAmount,
          showBudgetToMembers: data.showBudgetToMembers
        })
        toast.success('專案更新成功')
      } else {
        // 建立新專案
        await projectService.createProject(data)
        toast.success('專案建立成功')
      }
      
      setShowForm(false)
      setEditingProject(undefined)
      loadProjects()
    } catch (error) {
      console.error('Failed to submit project:', error)
      toast.error(editingProject ? '專案更新失敗' : '專案建立失敗')
    } finally {
      setFormLoading(false)
    }
  }

  // 處理刪除專案
  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('確定要刪除此專案嗎？此操作無法復原。')) {
      return
    }

    try {
      await projectService.deleteProject(projectId)
      toast.success('專案刪除成功')
      loadProjects()
    } catch (error) {
      console.error('Failed to delete project:', error)
      toast.error('專案刪除失敗')
    }
  }

  // 處理編輯專案
  const handleEditProject = (project: Project) => {
    setEditingProject(project)
    setShowForm(true)
  }

  // 處理查看專案詳情
  const handleViewDetail = (_projectId: string) => {
    // TODO: 實現專案詳情頁面
    toast('專案詳情頁面開發中', { icon: 'ℹ️' })
  }

  // 處理建立新專案
  const handleCreateProject = () => {
    setEditingProject(undefined)
    setShowForm(true)
  }

  // 處理取消表單
  const handleCancelForm = () => {
    setShowForm(false)
    setEditingProject(undefined)
  }

  // 處理專案狀態變更
  const handleStatusChange = async (projectId: string, isActive: boolean) => {
    try {
      await projectService.updateProject(projectId, { isActive })
      toast.success(`專案已${isActive ? '啟用' : '停用'}`)
      loadProjects()
    } catch (error) {
      console.error('Failed to update project status:', error)
      toast.error('專案狀態更新失敗')
    }
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-800">專案管理</h1>
        <p className="text-secondary-600 mt-2">管理您的專案、預算和團隊成員</p>
      </div>

      {/* 篩選器 */}
      <ProjectFilters
        filters={filters}
        onFiltersChange={setFilters}
        onCreateProject={handleCreateProject}
      />

      {/* 專案列表 */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4"></div>
              <div className="h-2 bg-gray-200 rounded mb-4"></div>
              <div className="flex space-x-2">
                <div className="h-8 bg-gray-200 rounded flex-1"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-800 mb-2">
            {filters.search ? '找不到符合條件的專案' : '還沒有專案'}
          </h3>
          <p className="text-secondary-600 mb-6">
            {filters.search 
              ? '請嘗試調整搜尋條件或篩選器'
              : '建立您的第一個專案來開始管理工時和預算'
            }
          </p>
          {!filters.search && (
            <button
              onClick={handleCreateProject}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 transition-colors font-medium"
            >
              建立第一個專案
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onViewDetail={handleViewDetail}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}

      {/* 專案表單 Modal */}
      {showForm && (
        <ProjectForm
          project={editingProject}
          users={users}
          onSubmit={handleSubmitProject}
          onCancel={handleCancelForm}
          isLoading={formLoading}
        />
      )}
    </div>
  )
}

export default ProjectsPage
